<template>
  <v-card class="mt-4">
    <v-card-title>
      YOMT 設定
      <v-spacer />
      <v-btn color="primary" @click="openSubTypeDialog">
        <v-icon left>mdi-folder-plus</v-icon>
        新增類型
      </v-btn>
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col v-for="subType in displaySubTypes" :key="subType.id" cols="12">
          <div class="mb-4">
            <h3 class="mb-3">
              <span>
                {{ subType.label }}
                <span v-if="subType.isTemporary" class="temporary-badge">暫存</span>
              </span>
              <div class="button-group">
                <v-btn
                  v-if="!subType.isTemporary"
                  x-small
                  icon
                  @click="openEditSubTypeDialog(subType)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn small color="primary" @click="openAddDialog(subType.id, subType.order)">
                  <v-icon small left>mdi-plus</v-icon>
                  新增
                </v-btn>
              </div>
            </h3>
            <OptionsList
              :type="'yomt'"
              :sub-type="subType.id"
              :initial-options="getOptionsForSubType(subType.id)"
              @reload="reload"
            />
          </div>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="addDialog" max-width="500px">
      <v-card>
        <v-card-title>新增選項</v-card-title>
        <v-card-text>
          <ValidationObserver ref="form" v-slot="{ invalid }">
            <ValidationProvider v-slot="{ errors }" name="名稱" rules="required">
              <v-text-field
                v-model="addForm.name"
                :error="!!errors.length"
                :error-messages="errors"
                label="名稱 *"
                outlined
                dense
              />
            </ValidationProvider>
            <ValidationProvider v-slot="{ errors }" name="排序" rules="numeric">
              <v-text-field
                v-model.number="addForm.order"
                :error="!!errors.length"
                :error-messages="errors"
                label="排序"
                outlined
                dense
                type="number"
                inputmode="numeric"
                hint="數字越小越靠前"
              />
            </ValidationProvider>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">取消</v-btn>
          <v-btn color="primary" @click="saveOption">儲存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add SubType Dialog -->
    <v-dialog v-model="subTypeDialog" max-width="500px">
      <v-card>
        <v-card-title>新增類型</v-card-title>
        <v-card-text>
          <ValidationObserver ref="subTypeForm" v-slot="{ invalid }">
            <ValidationProvider v-slot="{ errors }" name="類型名稱" rules="required">
              <v-text-field
                v-model="subTypeForm.subType"
                :error="!!errors.length"
                :error-messages="errors"
                label="類型名稱 *"
                outlined
                dense
                persistent-hint
              />
            </ValidationProvider>
            <ValidationProvider v-slot="{ errors }" name="類型排序" rules="required|numeric">
              <v-text-field
                v-model.number="subTypeForm.order"
                :error="!!errors.length"
                :error-messages="errors"
                label="類型排序 *"
                outlined
                dense
                type="number"
                class="mt-4"
                hint="數字越小越靠前"
                persistent-hint
              />
            </ValidationProvider>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeSubTypeDialog">取消</v-btn>
          <v-btn color="primary" @click="saveSubType">儲存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit SubType Dialog -->
    <v-dialog v-model="editSubTypeDialog" max-width="500px">
      <v-card>
        <v-card-title>編輯類型</v-card-title>
        <v-card-text>
          <ValidationObserver ref="editSubTypeForm" v-slot="{ invalid }">
            <ValidationProvider v-slot="{ errors }" name="類型名稱">
              <v-text-field
                v-model="editSubTypeForm.newSubType"
                :error="!!errors.length"
                :error-messages="errors"
                label="類型名稱"
                outlined
                dense
                persistent-hint
              />
            </ValidationProvider>
            <ValidationProvider v-slot="{ errors }" name="類型排序" rules="required|numeric">
              <v-text-field
                v-model.number="editSubTypeForm.newSubTypeOrder"
                :error="!!errors.length"
                :error-messages="errors"
                label="類型排序 *"
                outlined
                dense
                type="number"
                class="mt-4"
                hint="數字越小越靠前"
                persistent-hint
              />
            </ValidationProvider>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeEditSubTypeDialog">取消</v-btn>
          <v-btn color="primary" @click="updateSubTypeData">儲存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import OptionsList from '@/components/Options/OptionsList';
import { createOption, getOptions, updateSubType } from '@/api/options';
import api from '@/api';
import { mapActions } from 'vuex';

export default {
  name: 'YOMTOptions',
  components: {
    OptionsList,
  },
  data() {
    return {
      subTypes: [],
      temporarySubTypes: [], // { subType: 'name', order: 0 }
      allOptions: [], // Store all options to avoid multiple fetches
      addDialog: false,
      subTypeDialog: false,
      editSubTypeDialog: false,
      addForm: {
        type: 'yomt',
        subType: '',
        subTypeOrder: 0,
        name: '',
        order: 0,
      },
      subTypeForm: {
        subType: '',
        order: 0,
      },
      editSubTypeForm: {
        type: 'yomt',
        currentSubType: '',
        newSubType: '',
        newSubTypeOrder: 0,
      },
    };
  },
  computed: {
    displaySubTypes() {
      // Merge real and temporary subTypes
      const realSubTypes = this.subTypes.map(subType => ({
        id: subType,
        label: this.getSubTypeLabel(subType),
        order: this.getSubTypeOrder(subType),
        isTemporary: false,
      }));

      const tempSubTypes = this.temporarySubTypes.map(temp => ({
        id: temp.subType,
        label: temp.subType,
        order: temp.order,
        isTemporary: true,
      }));

      // Merge and sort by order
      return [...realSubTypes, ...tempSubTypes].sort((a, b) => a.order - b.order);
    },
  },
  async mounted() {
    await this.loadSubTypes();
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),
    getSubTypeLabel(subType) {
      return subType || '頂部內容';
    },
    getSubTypeOrder(subType) {
      const existingOptions = this.allOptions.filter(
        opt => opt.type === 'yomt' && opt.subType === subType,
      );
      return existingOptions.length > 0 ? existingOptions[0].subTypeOrder || 0 : 0;
    },
    getOptionsForSubType(subType) {
      // Filter options for specific subType and sort
      return this.allOptions
        .filter(opt => opt.type === 'yomt' && opt.subType === subType)
        .sort((a, b) => {
          if (a.order !== b.order) return a.order - b.order;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
    },
    async loadSubTypes() {
      try {
        const response = await getOptions(api, { filter: { type: 'yomt' } });
        const options = response.docs || response;

        // Store all options for reference (avoid multiple fetches)
        this.allOptions = options;

        // Get unique subTypes for YOMT sorted by subTypeOrder
        const yomtOptions = options;
        const yomtSubTypeMap = new Map();
        yomtOptions.forEach(opt => {
          const subType = opt.subType || '';
          if (!yomtSubTypeMap.has(subType)) {
            yomtSubTypeMap.set(subType, opt.subTypeOrder || 0);
          }
        });
        this.subTypes = Array.from(yomtSubTypeMap.entries())
          .sort((a, b) => a[1] - b[1])
          .map(entry => entry[0]);

        // Remove temporary subTypes that now exist in real data
        this.temporarySubTypes = this.temporarySubTypes.filter(
          temp => !this.subTypes.includes(temp.subType),
        );
      } catch (e) {
        console.error('Failed to load subTypes:', e);
      }
    },
    openSubTypeDialog() {
      this.subTypeForm = {
        subType: '',
        order: 0,
      };
      this.subTypeDialog = true;
    },
    closeSubTypeDialog() {
      this.subTypeDialog = false;
      this.$refs.subTypeForm?.reset();
    },
    openEditSubTypeDialog(subType) {
      this.editSubTypeForm = {
        type: 'yomt',
        currentSubType: subType.id,
        newSubType: subType.id,
        newSubTypeOrder: subType.order,
      };
      this.editSubTypeDialog = true;
    },
    closeEditSubTypeDialog() {
      this.editSubTypeDialog = false;
      this.$refs.editSubTypeForm?.reset();
    },
    async updateSubTypeData() {
      try {
        if (!(await this.$refs.editSubTypeForm?.validate?.())) {
          return;
        }

        await updateSubType(api, this.editSubTypeForm);
        await this.updateAlertMessage({
          msg: '更新成功',
          type: 'success',
          color: 'success',
        });

        this.closeEditSubTypeDialog();
        await this.loadSubTypes();
        this.reload();
      } catch (e) {
        await this.updateAlertMessage({
          msg: e.message || '更新失敗',
          type: 'error',
          color: 'error',
        });
      }
    },
    async saveSubType() {
      try {
        if (!(await this.$refs.subTypeForm?.validate?.())) {
          return;
        }

        // Check if subType already exists
        const isDuplicate =
          this.subTypes.includes(this.subTypeForm.subType) ||
          this.temporarySubTypes.some(temp => temp.subType === this.subTypeForm.subType);

        if (isDuplicate) {
          await this.updateAlertMessage({
            msg: '類型已存在',
            type: 'error',
            color: 'error',
          });
          return;
        }

        // Add to temporary subTypes
        this.temporarySubTypes.push({
          subType: this.subTypeForm.subType,
          order: this.subTypeForm.order,
        });

        await this.updateAlertMessage({
          msg: '類型已暫存，請新增選項以完成建立',
          type: 'success',
          color: 'success',
        });

        this.closeSubTypeDialog();
      } catch (e) {
        await this.updateAlertMessage({
          msg: e.message || '操作失敗',
          type: 'error',
          color: 'error',
        });
      }
    },
    openAddDialog(subType, subTypeOrder) {
      this.addForm = {
        type: 'yomt',
        subType,
        subTypeOrder,
        name: '',
        order: 0,
      };
      this.addDialog = true;
    },
    closeDialog() {
      this.addDialog = false;
      this.$refs.form?.reset();
    },
    async saveOption() {
      try {
        if (!(await this.$refs.form?.validate?.())) {
          return;
        }

        await createOption(api, this.addForm);
        await this.updateAlertMessage({
          msg: '新增成功',
          type: 'success',
          color: 'success',
        });

        this.closeDialog();
        await this.loadSubTypes();
        this.reload();
      } catch (e) {
        await this.updateAlertMessage({
          msg: e.message || '操作失敗',
          type: 'error',
          color: 'error',
        });
      }
    },
    reload() {
      // Trigger reload for all lists
      this.$root.$emit('reload-options');
    },
  },
};
</script>

<style scoped>
h3 {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.button-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.temporary-badge {
  font-size: 12px;
  color: #ff9800;
  font-weight: normal;
  margin-left: 8px;
  padding: 2px 8px;
  background-color: #fff3e0;
  border-radius: 4px;
}
</style>
